import { type FC, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router";
import { PlaybookConfigurationAssistant } from "../features/agents/playbookConfigurationAssistant/playbookConfigurationAssistant.js";
import { PlaybookEditorContainer } from "./playbookEditorContainer.js";
import { usePlaybookEditorOutletContext } from "./playbookEditorOutletContext.js";
import {
  getSelectionIdForAssessmentKey,
  LEGACY_CRITERION_QUERY_PARAM,
  PLAYBOOK_SELECTION_QUERY_PARAM,
  resolveEditPlaybookUrlState,
} from "./playbookEditorUrlState.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export const PlaybookEditPage: FC = () => {
  const context = usePlaybookEditorOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    playbookId,
    editorProps,
    assistantProps,
    onViewedSelectionChange,
    onStableSelectionChange,
  } = context;

  const urlState = useMemo(
    () => (data ? resolveEditPlaybookUrlState(searchParams, data.tree) : null),
    [data, searchParams],
  );
  const currentQuery = searchParams.toString();
  const canonicalQuery = urlState?.searchParams.toString();
  useEffect(() => {
    if (
      data &&
      canonicalQuery !== undefined &&
      canonicalQuery !== currentQuery
    ) {
      setSearchParams(
        (current) =>
          resolveEditPlaybookUrlState(current, data.tree).searchParams,
        { replace: true },
      );
    }
  }, [canonicalQuery, currentQuery, data, setSearchParams]);

  const handleCurrentSelectionChange = useCallback(
    (key: AssessmentTreeItemKey | undefined) => {
      onViewedSelectionChange(key);
      const selectionId = getSelectionIdForAssessmentKey(key);
      onStableSelectionChange(selectionId);

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(LEGACY_CRITERION_QUERY_PARAM);
      if (selectionId) {
        nextSearchParams.set(PLAYBOOK_SELECTION_QUERY_PARAM, selectionId);
      } else {
        nextSearchParams.delete(PLAYBOOK_SELECTION_QUERY_PARAM);
      }
      if (nextSearchParams.toString() !== searchParams.toString()) {
        setSearchParams(nextSearchParams, { replace: true });
      }
    },
    [
      onViewedSelectionChange,
      onStableSelectionChange,
      searchParams,
      setSearchParams,
    ],
  );

  const editor = (
    <PlaybookEditorContainer
      {...editorProps}
      initialSelectedKey={urlState?.selectionKey}
      onCurrentSelectionChange={handleCurrentSelectionChange}
    />
  );

  return playbookId === null ? (
    <div className="flex min-h-0 flex-1 flex-col p-2">{editor}</div>
  ) : (
    <div className="grid min-h-0 flex-1 grid-cols-[1fr_auto]">
      <div className="flex min-h-0 flex-col p-2">{editor}</div>
      {assistantProps ? (
        <div className="flex min-h-0 flex-col border-l border-border-light bg-white">
          <PlaybookConfigurationAssistant {...assistantProps} />
        </div>
      ) : null}
    </div>
  );
};
